/**
 * Decide whether a maneuver-map draw should fit the viewport.
 *
 * A type switch (tack → gybe) clears the selection, and that sets triggerUpdate before the
 * maneuver page has replaced `filtered` with the new type's events. The map then draws the
 * previous type and, if it fits that draw, consumes the recenter. The following draw of the
 * new type stays on the old viewport, so those tracks land off the map.
 *
 * The ids loaded at the moment of the switch are the stale set. A draw made only of those
 * ids must not fit. The draw of the new type's ids should.
 */

export function positiveEventIdsFromList(items: readonly unknown[]): number[] {
  const ids: number[] = [];
  for (const item of items) {
    const raw = typeof item === "number" ? item : (item as { event_id?: unknown } | null)?.event_id;
    const id = typeof raw === "number" ? raw : Number(raw);
    if (Number.isFinite(id) && id > 0) ids.push(id);
  }
  return ids;
}

export function positiveEventIdsFromTracks(
  tracks: readonly ({ event_id?: number } | null | undefined)[][] | null | undefined
): number[] {
  if (!tracks) return [];
  const ids: number[] = [];
  for (const track of tracks) {
    const id = track?.[0]?.event_id;
    if (typeof id === "number" && Number.isFinite(id) && id > 0) ids.push(id);
  }
  return ids;
}

export function collectFiniteLatLngs<T extends { lat: number; lng: number }>(
  tracks: readonly ({ LatLng?: T | null } | null | undefined)[][] | null | undefined
): T[] {
  if (!tracks) return [];
  const points: T[] = [];
  for (const track of tracks) {
    if (!track) continue;
    for (const point of track) {
      const latLng = point?.LatLng;
      if (latLng == null) continue;
      if (!Number.isFinite(latLng.lat) || !Number.isFinite(latLng.lng)) continue;
      points.push(latLng);
    }
  }
  return points;
}

/**
 * A map-marker click that changes maneuver type must not select until the maneuver map has
 * fitted every track of the new type. Selecting first makes that fit run against the old
 * type, or against the single clicked event, and the rest of the gybes land off screen.
 */
let pendingAfterRecenter: (() => void) | null = null;
let pendingAfterRecenterTimer: ReturnType<typeof setTimeout> | null = null;

export function resetPendingManeuverMapRecenter(): void {
  pendingAfterRecenter = null;
  if (pendingAfterRecenterTimer != null) {
    clearTimeout(pendingAfterRecenterTimer);
    pendingAfterRecenterTimer = null;
  }
}

/** Run `select` after the maneuver map fits the new type. Falls back if that fit never comes. */
export function selectAfterManeuverMapRecenter(select: () => void, fallbackMs = 4000): void {
  resetPendingManeuverMapRecenter();
  pendingAfterRecenter = select;
  pendingAfterRecenterTimer = setTimeout(() => {
    pendingAfterRecenterTimer = null;
    const fn = pendingAfterRecenter;
    pendingAfterRecenter = null;
    fn?.();
  }, fallbackMs);
}

/** The maneuver map calls this after it has fitted the full set of the new type. */
export function notifyManeuverMapRecentered(): void {
  const fn = pendingAfterRecenter;
  pendingAfterRecenter = null;
  if (pendingAfterRecenterTimer != null) {
    clearTimeout(pendingAfterRecenterTimer);
    pendingAfterRecenterTimer = null;
  }
  fn?.();
}

export function shouldRecenterManeuverMap(options: {
  shouldCenter: boolean;
  firstDraw: boolean;
  drawnEventIds: readonly number[];
  drawnPointCount: number;
  staleEventIds: ReadonlySet<number> | null;
}): boolean {
  if (options.drawnPointCount <= 0) return false;
  if (!options.shouldCenter && !options.firstDraw) return false;
  const stale = options.staleEventIds;
  if (stale != null && stale.size > 0 && options.drawnEventIds.length > 0) {
    const onlyPreviousType = options.drawnEventIds.every((id) => stale.has(id));
    if (onlyPreviousType) return false;
  }
  return true;
}
